/**
 * RecentCareLogsWidget Component
 *
 * Widget showing 3 most recent care log entries with severity indicators.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md - Phase 2 implementation.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { CareLogSummary } from '../carelogs';
import { BookOpen, FileText, Loader2, AlertCircle, ChevronRight, Calendar } from 'lucide-react';

interface ListCareLogsResponse {
  success: boolean;
  careLogs: CareLogSummary[];
  totalCount: number;
  error?: string;
}

interface RecentCareLogsWidgetProps {
  activeCaseFileId?: string;
  limit?: number;
}

// Severity level type
type SeverityLevel = 'routine' | 'notable' | 'concerning' | 'urgent';

// Severity styles for badges
const SEVERITY_STYLES: Record<SeverityLevel, { bg: string; text: string }> = {
  routine: { bg: 'bg-gray-100', text: 'text-gray-600' },
  notable: { bg: 'bg-blue-100', text: 'text-blue-700' },
  concerning: { bg: 'bg-amber-100', text: 'text-amber-700' },
  urgent: { bg: 'bg-red-100', text: 'text-red-700' },
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.getTime() === today.getTime()) {
    return 'Today';
  }
  if (date.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export function RecentCareLogsWidget({
  activeCaseFileId,
  limit = 3,
}: RecentCareLogsWidgetProps): JSX.Element {
  const navigate = useNavigate();
  const [careLogs, setCareLogs] = useState<CareLogSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecentLogs = async () => {
      if (!activeCaseFileId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const listCareLogs = httpsCallable<
          { caseFileId: string; limit?: number },
          ListCareLogsResponse
        >(functions, 'listCareLogs');

        const result = await listCareLogs({ caseFileId: activeCaseFileId, limit });

        if (result.data.success) {
          setCareLogs(result.data.careLogs);
          setTotalCount(result.data.totalCount);
        } else {
          throw new Error(result.data.error || 'Failed to load care logs');
        }
      } catch (err) {
        console.error('Error loading recent care logs:', err);
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentLogs();
  }, [activeCaseFileId, limit]);

  const handleViewAll = () => {
    if (activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/carelogs`);
    }
  };

  const handleLogClick = (_logId: string) => {
    if (activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/carelogs`);
    }
  };

  // Get display text (AI summary or truncated log text)
  const getDisplayText = (log: CareLogSummary): string => {
    if (log.summary) {
      return truncateText(log.summary, 100);
    }
    return truncateText(log.logText, 100);
  };

  return (
    <div className="card h-full">
      {/* Header */}
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-evergreen" />
          <h3 className="font-semibold text-ink">Recent Care Logs</h3>
        </div>
        {activeCaseFileId && (
          <button
            onClick={handleViewAll}
            className="btn-ghost btn-sm text-evergreen hover:text-deep-forest flex items-center gap-1"
          >
            View All
            <ChevronRight size={16} />
          </button>
        )}
      </div>

      <div className="card-body">
        {/* No CaseFile selected */}
        {!activeCaseFileId && !loading && (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-soft-neutral flex items-center justify-center">
              <FileText size={24} className="text-muted" />
            </div>
            <p className="text-muted text-sm">Select a care recipient</p>
            <p className="text-xs text-muted mt-1">to view recent care logs</p>
          </div>
        )}

        {/* Loading state */}
        {loading && activeCaseFileId && (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-evergreen" />
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex items-center gap-2 text-red-600 py-4">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && activeCaseFileId && careLogs.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-soft-neutral flex items-center justify-center">
              <FileText size={24} className="text-muted" />
            </div>
            <p className="text-muted text-sm">No care logs yet</p>
            <p className="text-xs text-muted mt-1">Start logging daily activities</p>
          </div>
        )}

        {/* Care logs list */}
        {!loading && !error && careLogs.length > 0 && (
          <div className="space-y-2">
            {careLogs.map((log) => {
              const severityLevel = (log.severityLevel || 'routine') as SeverityLevel;
              const severityStyle = SEVERITY_STYLES[severityLevel];
              const showSeverityBadge = severityLevel !== 'routine';

              return (
                <button
                  key={log.logId}
                  onClick={() => handleLogClick(log.logId)}
                  className={`w-full text-left p-3 rounded-lg hover:bg-soft-neutral transition-colors ${
                    showSeverityBadge ? `border-l-4 ${severityLevel === 'urgent' ? 'border-red-400' : severityLevel === 'concerning' ? 'border-amber-400' : 'border-blue-400'}` : 'border-l-4 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(log.logDate)}
                      </span>
                      {showSeverityBadge && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityStyle.bg} ${severityStyle.text}`}>
                          {severityLevel}
                        </span>
                      )}
                    </div>
                    {log.inputType === 'voice' && (
                      <span className="text-xs text-muted bg-soft-neutral px-1.5 py-0.5 rounded">
                        Voice
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ink line-clamp-2">
                    {getDisplayText(log)}
                  </p>
                </button>
              );
            })}

            {/* Show more indicator */}
            {totalCount > careLogs.length && (
              <button
                onClick={handleViewAll}
                className="w-full text-center text-sm text-evergreen hover:text-deep-forest pt-2"
              >
                +{totalCount - careLogs.length} more entr{totalCount - careLogs.length > 1 ? 'ies' : 'y'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentCareLogsWidget;
